import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import TaskList from "../components/operation/assignWorker/TaskList";
import MainCrewPage from "../components/operation/assignWorker/MainCrewPage";
import FilterScreen from "../components/operation/assignWorker/FilterScreen";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  addTrackWorkerAction,
  getTrackWorkerDetailsAction,
  setAddTrackWorkerAction,
} from "../../store/reducers/assignWorkerSlice";

// How often each open browser/tab re-checks the track-worker status.
// There is no websocket/real-time sync in this app, so this polling is what
// allows a browser to detect "I was cleared out by the owner on another
// browser" and disable itself accordingly.
const POLL_INTERVAL_MS = 10000;

const ClientOperationAssignWorker = () => {
  const today = dayjs().format("YYYY-MM-DD");
  const dispatch = useDispatch();
  const { operationTasks, trackWorkerDetails, addTrackWorkerResponse } =
    useSelector((state: any) => state.assignWorker);
  const { user } = useSelector((state: any) => state?.user);

  // ---------------------------------------------------------------------
  // STATE DESIGN NOTE:
  // We used to have a single `isEditable` flag controlled by two unrelated
  // concerns (multi-tab locking AND "another worker is IN" conflict).
  // Any effect that fired for one concern could accidentally overwrite the
  // other concern's result (e.g. window focus re-running the tab-lock check
  // would wrongly clear the worker-conflict warning).
  // Fix: keep two independent flags and derive `isEditable` from both.
  // ---------------------------------------------------------------------

  // Concern 1: is this browser TAB blocked because another tab (same user) owns the lock?
  const [isTabLocked, setIsTabLocked] = useState(false);
  const [multiTabWarning, setMultiTabWarning] = useState(false);

  // Concern 2: is another USER currently checked "IN" on this record?
  const [isWorkerConflict, setIsWorkerConflict] = useState(false);

  // Derived value: page is editable only when neither block applies
  const isEditable = !isTabLocked && !isWorkerConflict;

  const [isViewOnly, setIsViewOnly] = useState(false);
  const [isClearingViewers, setIsClearingViewers] = useState(true);
  const [isClearViewersPending, setIsClearViewersPending] = useState(false);

  // Tracks whether we already auto-checked-in for the CURRENT filteredData.date.
  // Prevents the CASE-2 auto check-in effect from firing repeatedly / looping
  // every time trackWorkerDetails changes.
  const hasAutoCheckedIn = useRef(false);

  // Tracks whether THIS user was the one actively checked "IN" before the
  // latest poll/refetch. Used to detect if we just got kicked out by
  // someone else (e.g. owner clicked "Clear Viewers" from another browser).
  const wasCurrentUserInRef = useRef(false);
  const hasReceivedInitialWorkerResponse = useRef(false);

  const [filteredData, setFilteredData] = useState({
    search: "",
    date: today,
    taskType: "",
  });

  // ---------------------------------------------------------------------
  // FIX: stale-closure guard.
  // The "check current user OUT on unmount" effect below has an empty `[]`
  // dependency array, so its cleanup function only ever "sees" the values
  // of trackWorkerDetails/filteredData that existed on the FIRST render.
  // On a first-ever visit that's usually an empty array (API hasn't
  // resolved yet) -> id ends up null on checkout.
  // These refs are updated on every render, so the cleanup function can
  // read the LATEST value via .current instead of the stale closed-over one.
  // ---------------------------------------------------------------------
  const trackWorkerDetailsRef = useRef(trackWorkerDetails);
  const filteredDataRef = useRef(filteredData);

  useEffect(() => {
    trackWorkerDetailsRef.current = trackWorkerDetails;
  }, [trackWorkerDetails]);

  useEffect(() => {
    filteredDataRef.current = filteredData;
  }, [filteredData]);

  // ---------------------------------------------------------------------
  // Multi-tab prevention logic (Concern 1 only — never touches isWorkerConflict)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!user?.user?.staff_id) return;
    const tabKey = `assignWorkerTab_${user.user.staff_id}`;
    // Always generate a new tabId for every tab (even duplicates of the same page)
    const tabId = `${Date.now()}_${Math.random()}`;
    sessionStorage.setItem(tabKey, tabId);

    // Only the first tab to open gets the lock; duplicates are disabled
    if (!localStorage.getItem(tabKey)) {
      localStorage.setItem(tabKey, tabId); // First tab takes the lock
    }

    // Checks ONLY the tab-lock state. Does not know/care about worker conflicts.
    const checkEditable = () => {
      const current = localStorage.getItem(tabKey);
      if (current === tabId) {
        setIsTabLocked(false);
        setMultiTabWarning(false);
      } else {
        setIsTabLocked(true);
        setMultiTabWarning(true);
      }
    };

    checkEditable();

    // Listen for changes from other tabs
    const onStorage = (e: any) => {
      if (e.key === tabKey) {
        checkEditable();
      }
    };
    window.addEventListener("storage", onStorage);
    // Also re-check the lock on focus and visibility change
    window.addEventListener("focus", checkEditable);
    document.addEventListener("visibilitychange", checkEditable);

    // Cleanup function for both beforeunload and React unmount
    const cleanup = () => {
      const val = localStorage.getItem(tabKey);
      if (val === tabId) {
        localStorage.removeItem(tabKey);
      }
    };
    window.addEventListener("beforeunload", cleanup);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", checkEditable);
      document.removeEventListener("visibilitychange", checkEditable);
      window.removeEventListener("beforeunload", cleanup);
      cleanup();
    };
    // eslint-disable-next-line
  }, [user?.user?.staff_id]);

  // ---------------------------------------------------------------------
  // Fetch track-worker details whenever the selected date changes.
  // (Previously this only ran once on mount, so switching dates in
  // FilterScreen never re-evaluated the worker-conflict state.)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!filteredData?.date) return;

    hasAutoCheckedIn.current = false;
    wasCurrentUserInRef.current = false;

    setIsClearingViewers(true);

    dispatch(getTrackWorkerDetailsAction(today));
  }, [filteredData?.date, dispatch]);

  // ---------------------------------------------------------------------
  // Handle the response of addTrackWorkerAction.
  // addTrackWorkerResponse is a plain boolean: true = success, false = error/initial.
  // We branch based on whether we were waiting for a "Clear Viewers" call,
  // because that flow needs different handling (success vs failure) than
  // the normal check-in/check-out flow.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!isClearViewersPending) {
      // Normal (non clear-viewers) add/checkout flow
      if (!addTrackWorkerResponse) return;
      dispatch(getTrackWorkerDetailsAction(today));
      dispatch(setAddTrackWorkerAction(false));
      return;
    }

    // We ARE waiting for a "Clear Viewers" response.
    // The saga always dispatches setAddTrackWorkerAction(true|false) once the
    // request settles, so any change here means "request finished".
    if (addTrackWorkerResponse === true) {
      // SUCCESS: refetch details, this user becomes the active worker, hide warning
      dispatch(getTrackWorkerDetailsAction(today));
      setIsViewOnly(false);
      setIsWorkerConflict(false); // hides the Alert in MainCrewPage
      setIsClearingViewers(true); // re-arm CASE 1/2 so this user gets registered as "IN"
      hasAutoCheckedIn.current = false; // allow one fresh auto check-in for this user
      wasCurrentUserInRef.current = false;
    }
    // FAILURE (addTrackWorkerResponse === false): do nothing to isWorkerConflict —
    // the warning stays exactly as it was.

    // Either way (success or failure), the pending request is done.
    // This reset is critical: without it, the button stays disabled forever
    // after any failed "Clear Viewers" call.
    setIsClearViewersPending(false);
    dispatch(setAddTrackWorkerAction(false)); // clear response so it isn't reprocessed
  }, [addTrackWorkerResponse]);

  // ---------------------------------------------------------------------
  // Evaluate track worker details: decide if there's a conflict with another user.
  // Also detects "I was checked IN before, but now the record is gone/changed"
  // which means someone else cleared me out from a different browser/tab.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const currentUserId = user?.user?.staff_id;

    if (!isClearingViewers || !currentUserId) {
      return;
    }

    const workers = Array.isArray(trackWorkerDetails) ? trackWorkerDetails : [];

    console.log("101 workers", workers);
    console.log("101 currentUserId", currentUserId);
    console.log("101 hasAutoCheckedIn", hasAutoCheckedIn.current);
    console.log("101 wasCurrentUserIn", wasCurrentUserInRef.current);

    // =====================================================
    // CASE 1: Worker exists
    // =====================================================

    if (workers.length > 0) {
      const worker = workers[0];

      const workerUserId = worker?.user_id;
      const workerStatus = worker?.status;

      console.log("101 workerUserId", workerUserId);
      console.log("101 workerStatus", workerStatus);

      // -----------------------------------------------
      // Current user is already IN
      // -----------------------------------------------

      if (
        String(workerUserId) === String(currentUserId) &&
        workerStatus === "IN"
      ) {
        console.log("101 CURRENT USER IS IN");

        setIsWorkerConflict(false);

        wasCurrentUserInRef.current = true;
        hasAutoCheckedIn.current = true;

        return;
      }

      // -----------------------------------------------
      // Another user is IN
      // -----------------------------------------------

      if (workerStatus === "IN") {
        console.log("101 ANOTHER USER IS IN");

        setIsWorkerConflict(true);

        wasCurrentUserInRef.current = false;
        hasAutoCheckedIn.current = true;

        return;
      }

      // -----------------------------------------------
      // Worker exists but not IN
      // -----------------------------------------------

      setIsWorkerConflict(false);

      wasCurrentUserInRef.current = false;
      hasAutoCheckedIn.current = true;

      return;
    }

    // =====================================================
    // CASE 2: No worker exists
    // =====================================================

    console.log("101 NO ACTIVE WORKER");

    // -----------------------------------------------
    // Current user was previously IN
    // -----------------------------------------------

    if (wasCurrentUserInRef.current) {
      console.log("101 CURRENT USER WAS CLEARED");

      // If our previous session was cleared and there are now no active
      // workers, allow a fresh auto-login attempt instead of blocking.
      setIsWorkerConflict(false);

      wasCurrentUserInRef.current = false;
      hasAutoCheckedIn.current = false;
    }

    // -----------------------------------------------
    // Nobody is logged in
    // -----------------------------------------------

    if (!hasAutoCheckedIn.current) {
      console.log("101 NO USER -> AUTO LOGIN");

      hasAutoCheckedIn.current = true;

      setIsWorkerConflict(false);

      dispatch(
        addTrackWorkerAction({
          date: filteredData.date,
          id: null,
          start_date_time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          end_date_time: "",
          status: "IN",
        }),
      );
    }
  }, [
    trackWorkerDetails,
    isClearingViewers,
    user?.user?.staff_id,
    filteredData?.date,
    dispatch,
  ]);
  /// ---------------------------------------------------------------------
  // Run API call when leaving this page (check the current user OUT)
  //
  // FIX: reads from trackWorkerDetailsRef / filteredDataRef (always latest)
  // instead of the stale closed-over trackWorkerDetails / filteredData.
  // This is what previously caused `id` to be null the FIRST time a user
  // visited the page (because on first render trackWorkerDetails was still
  // empty/undefined, and that stale value got "locked in" by the empty
  // dependency array below).
  // ---------------------------------------------------------------------
  useEffect(() => {
    return () => {
      const latestTrackWorkerDetails = trackWorkerDetailsRef.current;
      const latestFilteredData = filteredDataRef.current;

      dispatch(
        addTrackWorkerAction({
          date: latestFilteredData?.date,
          id: latestTrackWorkerDetails?.[0]?.id ?? null,
          start_date_time:
            latestTrackWorkerDetails && latestTrackWorkerDetails.length > 0
              ? latestTrackWorkerDetails[0]?.start_date_time
              : dayjs().format("YYYY-MM-DD HH:mm:ss"),
          end_date_time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          status: "OUT",
        }),
      );
    };
    // eslint-disable-next-line
  }, []);

  const handleClearViewers = () => {
    // Prevent duplicate/parallel calls while one is already in flight
    if (isClearViewersPending) return;

    dispatch(setAddTrackWorkerAction(false)); // clear any stale response first
    setIsClearViewersPending(true);

    dispatch(
      addTrackWorkerAction({
        date: filteredData?.date,
        id: trackWorkerDetails?.[0]?.id ?? null,
        start_date_time:
          trackWorkerDetails && trackWorkerDetails.length > 0
            ? trackWorkerDetails[0]?.start_date_time
            : dayjs().format("YYYY-MM-DD HH:mm:ss"),
        end_date_time: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        status: "OUT",
        clearUsers: true,
      }),
    );
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 1, md: 1 }}
          sx={{ background: "#f4f4f4" }}
        >
          <Grid item xs={12} md={12}>
            {multiTabWarning && (
              <Box
                sx={{
                  background: "#fff3cd",
                  color: "#856404",
                  p: 2,
                  mb: 2,
                  border: "1px solid #ffeeba",
                  borderRadius: 2,
                  zIndex: 10,
                  position: "relative",
                }}
              >
                <Typography variant="body1">
                  This page is currently active in another tab. Editing and
                  assignment actions have been disabled in this tab to avoid
                  duplicate updates or data conflicts.
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                bgcolor: "#FFF",
                height: { xs: "auto", sm: "100%" },
                border: "1px solid #d1cfcf",
                borderRadius: "5px",
                padding: "16px 0px",
                opacity: 1,
                position: "relative",
                transition: "filter 0.2s",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                py={0.5}
                sx={{
                  alignItems: "center",
                  justifyItems: "center",
                  marginBottom: "0px",
                }}
              >
                <Box display="flex" justifyContent="center">
                  <Typography
                    variant="h5"
                    color="#009688"
                    fontWeight={600}
                    align="center"
                  >
                    Assign Worker
                  </Typography>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  mb={1}
                  gap={2}
                  sx={{ width: "300px", marginRight: "60px" }}
                >
                  {/* FilterScreen always enabled */}
                  <FilterScreen
                    filteredData={filteredData}
                    setFilteredData={setFilteredData}
                  />
                </Box>
              </Box>

              <Box sx={{ opacity: 1 }}>
                <Grid
                  container
                  rowSpacing={1}
                  columnSpacing={{ xs: 1, sm: 2, md: 12 }}
                  sx={{ padding: "0px 10px" }}
                >
                  <Grid
                    item
                    xs={12}
                    md={12}
                    sx={{
                      paddingLeft: "0px !important",
                      paddingTop: "0px !important",
                    }}
                  >
                    <TaskList />
                  </Grid>
                  {operationTasks && operationTasks?.length === 0 ? (
                    ""
                  ) : (
                    <Grid
                      item
                      xs={12}
                      md={12}
                      sx={{
                        paddingLeft: "0px !important",
                        marginTop: "10px",
                        background: "#E5F4F1",
                        borderRadius: "5px",
                        overflow: "hidden",
                        border: "1px solid #d1cfcf",
                        pointerEvents: multiTabWarning ? "none" : undefined,
                      }}
                    >
                      <MainCrewPage
                        filteredData={filteredData}
                        isEditable={isEditable}
                        isViewOnly={isViewOnly}
                        isClearViewersPending={isClearViewersPending}
                        handleClearViewers={handleClearViewers}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>

            {multiTabWarning && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 9,
                  background: "rgba(255,255,255,0.5)",
                  pointerEvents: "all",
                }}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ClientOperationAssignWorker;
